/** Client for the TokDraw NestJS API. */

const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ?? (import.meta.env.DEV ? 'http://localhost:3000' : '')
).replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

/** Creator headers are percent-encoded UTF-8 (names are often Burmese or emoji). */
function decodedHeader(res, name) {
  const value = res.headers.get(name);
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return '';
  }
}

/**
 * POST /api/comments → { comments, videoId, source, total, creator }
 * `creator` is `{ username, displayName, avatar }` or null when the server couldn't find the author.
 * @param {{url?: string, demo?: boolean, signal?: AbortSignal}} options
 */
export async function loadComments({ url, demo = false, signal } = {}) {
  const body = demo ? { demo: true, ...(url ? { url } : {}) } : { url };

  let res;
  try {
    res = await fetch(`${API_BASE}/api/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new ApiError(
      `Can't reach the TokDraw API at ${API_BASE || location.origin}. Is the backend running (npm run dev)?`,
      0,
    );
  }

  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    if (res.status === 429) throw new ApiError('Too many requests — wait a minute and try again.', 429);
    const message = Array.isArray(payload?.message) ? payload.message.join(' · ') : payload?.message;
    throw new ApiError(message || `Request failed (HTTP ${res.status}).`, res.status);
  }
  if (!Array.isArray(payload)) throw new ApiError('The API returned an unexpected response.', res.status);

  const creatorUsername = decodedHeader(res, 'X-Creator-Username');
  return {
    comments: payload,
    videoId: res.headers.get('X-Video-Id') ?? '',
    source: res.headers.get('X-Comments-Source') ?? (demo ? 'mock' : 'tiktok'),
    total: Number(res.headers.get('X-Comments-Total')) || payload.length,
    creator: creatorUsername
      ? {
          username: creatorUsername,
          displayName: decodedHeader(res, 'X-Creator-Name'),
          avatar: decodedHeader(res, 'X-Creator-Avatar'),
        }
      : null,
  };
}
